/**
 * McLaren MCL39 Click System – Landing
 * Start lights, reveals, sector progress, pit-stop game, driver switch,
 * onboard cams, display views, add-to-cart, sticky bar. No dependencies.
 */

(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const LANG = document.documentElement.lang || 'de';
  const NUM = new Intl.NumberFormat(LANG, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmt = (ms) => NUM.format(ms / 1000);

  function boot() {
    if (!document.querySelector('.m39')) return;
    if (!REDUCED) document.documentElement.classList.add('m39-js');

    initReveals();
    document.querySelectorAll('[data-m39-hero]').forEach(initHero);
    document.querySelectorAll('[data-m39-lap]').forEach(initLap);
    document.querySelectorAll('[data-m39-pit]').forEach(initPit);
    document.querySelectorAll('[data-m39-drivers]').forEach(initDrivers);
    document.querySelectorAll('[data-m39-cams]').forEach(initCams);
    document.querySelectorAll('[data-m39-display]').forEach(initDisplay);
    document.querySelectorAll('[data-m39-form]').forEach(initForm);
    initSticky();
  }

  /* ---------- Reveals ---------- */
  function initReveals() {
    const nodes = document.querySelectorAll('[data-m39-reveal]');
    if (!nodes.length) return;
    if (REDUCED || !('IntersectionObserver' in window)) {
      nodes.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    nodes.forEach((el) => io.observe(el));
  }

  /* ---------- Hero start lights ---------- */
  function initHero(hero) {
    if (REDUCED) return;
    const lamps = [...hero.querySelectorAll('.m39-hero__lamp')];
    const STEP = 260;
    lamps.forEach((lamp, i) => {
      setTimeout(() => lamp.classList.add('is-on'), 350 + i * STEP);
    });
    setTimeout(() => {
      lamps.forEach((lamp) => lamp.classList.remove('is-on'));
      hero.classList.add('is-go');
    }, 350 + lamps.length * STEP + 550);
  }

  /* ---------- Lap / sectors ---------- */
  function initLap(root) {
    const steps = [...root.querySelectorAll('[data-m39-step]')];
    const frames = [...root.querySelectorAll('[data-m39-frame]')];
    const segs = [...root.querySelectorAll('[data-m39-seg]')];
    const car = root.querySelector('[data-m39-lap-car]');
    const bar = root.querySelector('.m39-lap__bar');
    const body = root.querySelector('.m39-lap__body');
    if (!steps.length || !body) return;

    let active = -1;
    let ticking = false;

    const update = () => {
      ticking = false;
      const vh = window.innerHeight;
      const rect = body.getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, (vh * 0.55 - rect.top) / rect.height));

      let next = 0;
      let bestDist = Infinity;
      steps.forEach((step, i) => {
        const r = step.getBoundingClientRect();
        const dist = Math.abs(r.top + r.height / 2 - vh * 0.5);
        if (dist < bestDist) {
          bestDist = dist;
          next = i;
        }
      });

      if (next !== active) {
        active = next;
        steps.forEach((s, i) => s.classList.toggle('is-active', i === active));
        frames.forEach((f, i) => f.classList.toggle('is-active', i === active));
      }

      const finished = progress > 0.96;
      segs.forEach((seg, i) => {
        const done = finished || i < active;
        seg.classList.toggle('is-done', done);
        seg.classList.toggle('is-current', !done && i === active && progress > 0);
        seg.classList.toggle('is-purple', finished && i === segs.length - 1);
      });

      if (car && bar) {
        const width = bar.clientWidth - 34;
        car.style.left = `${Math.round(progress * width)}px`;
      }
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ---------- Pit-stop challenge ---------- */
  function initPit(root) {
    const copyEl = root.querySelector('[data-m39-pit-copy]');
    const copy = copyEl ? JSON.parse(copyEl.textContent) : {};
    const nuts = [...root.querySelectorAll('[data-m39-nut]')];
    const stage = root.querySelector('[data-m39-pit-stage]');
    const timeEl = root.querySelector('[data-m39-pit-time]');
    const statusEl = root.querySelector('[data-m39-pit-status]');
    const bestEl = root.querySelector('[data-m39-pit-best]');
    const overlay = root.querySelector('[data-m39-pit-overlay]');
    const startBtn = root.querySelector('[data-m39-pit-start]');
    const lights = root.querySelector('[data-m39-pit-lights]');
    const lamps = lights ? [...lights.querySelectorAll('i')] : [];
    const result = root.querySelector('[data-m39-pit-result]');
    const resultTime = root.querySelector('[data-m39-pit-result-time]');
    const verdictEl = root.querySelector('[data-m39-pit-verdict]');
    const noteEl = root.querySelector('[data-m39-pit-note]');
    const againBtn = root.querySelector('[data-m39-pit-again]');
    if (!stage || nuts.length !== 4) return;

    const KEY = 'm39-pitstop-best';
    const penaltyMs = Number(copy.penalty) || 500;
    let best = null;
    try {
      const stored = parseFloat(localStorage.getItem(KEY));
      if (stored > 0) best = stored;
    } catch (e) {}

    let state = 'idle';
    let order = [];
    let step = 0;
    let t0 = 0;
    let penalty = 0;
    let raf = 0;
    let timers = [];

    const later = (fn, ms) => timers.push(setTimeout(fn, ms));
    const clearTimers = () => {
      timers.forEach(clearTimeout);
      timers = [];
    };
    const setStatus = (text, mod) => {
      statusEl.textContent = text;
      statusEl.classList.toggle('is-go', mod === 'go');
      statusEl.classList.toggle('is-bad', mod === 'bad');
    };
    const renderBest = () => {
      bestEl.textContent = best ? `${fmt(best)} s` : '–';
    };
    const shuffle = (arr) => {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    };
    const resetLights = () => {
      lights.classList.remove('is-visible', 'is-green');
      lamps.forEach((l) => l.classList.remove('is-red'));
    };
    const resetNuts = () => {
      nuts.forEach((n) => {
        n.classList.remove('is-target', 'is-done', 'is-wrong');
        n.disabled = true;
      });
    };
    const setTarget = () => {
      nuts.forEach((n, i) => n.classList.toggle('is-target', i === order[step]));
    };
    const tick = () => {
      timeEl.textContent = fmt(performance.now() - t0 + penalty);
      raf = requestAnimationFrame(tick);
    };

    function start() {
      clearTimers();
      cancelAnimationFrame(raf);
      state = 'countdown';
      overlay.hidden = true;
      result.hidden = true;
      resetNuts();
      resetLights();
      timeEl.textContent = fmt(0);
      setStatus(copy.ready || '');
      lights.classList.add('is-visible');
      lamps.forEach((lamp, i) => later(() => lamp.classList.add('is-red'), 300 + i * 450));
      const goAt = 300 + lamps.length * 450 + 300 + Math.random() * 900;
      later(go, goAt);
    }

    function go() {
      state = 'run';
      lights.classList.add('is-green');
      setStatus(copy.go || '', 'go');
      order = shuffle([0, 1, 2, 3]);
      step = 0;
      penalty = 0;
      nuts.forEach((n) => (n.disabled = false));
      setTarget();
      t0 = performance.now();
      raf = requestAnimationFrame(tick);
      later(resetLights, 700);
    }

    function falseStart() {
      clearTimers();
      state = 'idle';
      resetLights();
      setStatus(copy.falseStart || '', 'bad');
      later(() => {
        overlay.hidden = false;
        startBtn.focus({ preventScroll: true });
      }, 1100);
    }

    function finish() {
      state = 'done';
      cancelAnimationFrame(raf);
      const total = performance.now() - t0 + penalty;
      timeEl.textContent = fmt(total);
      nuts.forEach((n) => {
        n.disabled = true;
        n.classList.remove('is-target');
      });

      const tier = total <= Number(copy.tierFast) ? 'fast' : total <= Number(copy.tierGood) ? 'good' : 'slow';
      const verdict = tier === 'fast' ? copy.verdictFast : tier === 'good' ? copy.verdictGood : copy.verdictSlow;
      const isBest = !best || total < best;
      if (isBest) {
        best = total;
        try {
          localStorage.setItem(KEY, String(Math.round(total)));
        } catch (e) {}
        renderBest();
      }

      const notes = [];
      if (isBest) notes.push(copy.newBest);
      if (penalty > 0) notes.push(`${copy.penaltyNote} +${fmt(penalty)} s`);

      resultTime.textContent = `${fmt(total)} s`;
      verdictEl.textContent = verdict || '';
      verdictEl.dataset.tier = tier;
      noteEl.textContent = notes.filter(Boolean).join(' · ');
      setStatus(verdict || '', tier === 'slow' ? '' : 'go');
      later(() => {
        result.hidden = false;
        againBtn.focus({ preventScroll: true });
      }, 350);
    }

    nuts.forEach((nut) => {
      nut.addEventListener('pointerdown', (e) => {
        if (state !== 'run') return;
        e.preventDefault();
        hit(nut);
      });
      nut.addEventListener('click', (e) => {
        // Keyboard activation (pointer hits are handled on pointerdown for speed)
        if (state !== 'run' || e.detail !== 0) return;
        hit(nut);
      });
    });

    function hit(nut) {
      const idx = Number(nut.dataset.m39Nut);
      if (nut.classList.contains('is-done')) return;
      if (idx === order[step]) {
        nut.classList.remove('is-target');
        nut.classList.add('is-done');
        nut.disabled = true;
        step += 1;
        if (step === order.length) {
          finish();
        } else {
          setTarget();
          setStatus(copy.go || '', 'go');
        }
      } else {
        penalty += penaltyMs;
        nut.classList.remove('is-wrong');
        void nut.offsetWidth;
        nut.classList.add('is-wrong');
        setStatus(copy.wrong || '', 'bad');
        later(() => nut.classList.remove('is-wrong'), 320);
      }
    }

    stage.addEventListener('pointerdown', (e) => {
      if (state === 'countdown' && !e.target.closest('[data-m39-pit-start]')) falseStart();
    });

    startBtn.addEventListener('click', start);
    againBtn.addEventListener('click', start);
    timeEl.textContent = fmt(0);
    renderBest();
  }

  /* ---------- Drivers ---------- */
  function initDrivers(root) {
    const btns = [...root.querySelectorAll('[data-m39-driver]')];
    const nums = [...root.querySelectorAll('[data-m39-driver-num]')];
    const cards = [...root.querySelectorAll('[data-m39-driver-card]')];
    btns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const i = btn.dataset.m39Driver;
        btns.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        nums.forEach((n) => n.classList.toggle('is-active', n.dataset.m39DriverNum === i));
        cards.forEach((c) => {
          const on = c.dataset.m39DriverCard === i;
          c.hidden = !on;
          c.classList.toggle('is-active', on);
        });
      });
    });
  }

  /* ---------- Onboard cams ---------- */
  function initCams(root) {
    const tabs = [...root.querySelectorAll('[data-m39-cam]')];
    const shots = [...root.querySelectorAll('[data-m39-shot]')];
    const label = root.querySelector('[data-m39-cam-label]');

    const select = (i, focus) => {
      tabs.forEach((t, k) => {
        const on = k === i;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
      });
      shots.forEach((s, k) => {
        const on = k === i;
        s.hidden = !on;
        s.classList.remove('is-active');
        if (on) {
          void s.offsetWidth;
          s.classList.add('is-active');
        }
      });
      if (label) label.textContent = tabs[i].querySelector('.m39-cams__tab-title').textContent;
      if (focus) tabs[i].focus();
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(i));
      tab.addEventListener('keydown', (e) => {
        const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
        if (e.key in keys) {
          e.preventDefault();
          select((i + keys[e.key] + tabs.length) % tabs.length, true);
        } else if (e.key === 'Home') {
          e.preventDefault();
          select(0, true);
        } else if (e.key === 'End') {
          e.preventDefault();
          select(tabs.length - 1, true);
        }
      });
    });
  }

  /* ---------- Display views ---------- */
  function initDisplay(root) {
    const btns = [...root.querySelectorAll('[data-m39-view-btn]')];
    const views = [...root.querySelectorAll('[data-m39-view]')];
    btns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const i = btn.dataset.m39ViewBtn;
        btns.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        views.forEach((v) => v.classList.toggle('is-active', v.dataset.m39View === i));
      });
    });
  }

  /* ---------- Add to cart ---------- */
  function initForm(form) {
    const btn = form.querySelector('[data-m39-add]');
    const msg = form.querySelector('[data-m39-msg]');
    if (!btn) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (btn.disabled) return;
      const original = btn.innerHTML;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      if (msg) {
        msg.textContent = '';
        msg.classList.remove('is-ok');
      }
      try {
        const routes = window.themeVariables?.routes || {};
        const res = await fetch(routes.cartAddUrl ? `${routes.cartAddUrl}.js` : '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.description || '');
        }
        const cart = await (await fetch(routes.cartUrl ? `${routes.cartUrl}.js` : '/cart.js')).json();
        const openDrawer = window.themeVariables?.settings?.cartType === 'drawer';
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, openMiniCart: openDrawer } })
        );
        btn.innerHTML = `<span>${btn.dataset.labelAdded}</span>`;
        if (openDrawer) {
          const toggle = document.querySelector('[data-action="toggle-drawer"][data-drawer="cart"]');
          if (toggle && !toggle.classList.contains('is-active')) setTimeout(() => toggle.click(), 120);
        }
        setTimeout(() => {
          btn.innerHTML = original;
          btn.disabled = false;
        }, 2400);
      } catch (err) {
        btn.innerHTML = original;
        btn.disabled = false;
        if (msg) msg.textContent = err.message || btn.dataset.labelError;
      } finally {
        btn.removeAttribute('aria-busy');
      }
    });
  }

  /* ---------- Sticky buy bar ---------- */
  function initSticky() {
    const bar = document.querySelector('[data-m39-sticky]');
    const hero = document.querySelector('[data-m39-hero]');
    const buy = document.querySelector('[data-m39-buy]');
    const finale = document.querySelector('.m39-finale');
    if (!bar || !('IntersectionObserver' in window)) return;

    let heroVisible = true;
    let buyVisible = false;
    let finaleVisible = false;
    const sync = () => {
      const show = !heroVisible && !buyVisible && !finaleVisible;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      const link = bar.querySelector('a');
      if (link) link.tabIndex = show ? 0 : -1;
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === hero) heroVisible = entry.isIntersecting;
        if (entry.target === buy) buyVisible = entry.isIntersecting;
        if (entry.target === finale) finaleVisible = entry.isIntersecting;
      });
      sync();
    });
    if (hero) io.observe(hero);
    else heroVisible = false;
    if (buy) io.observe(buy);
    if (finale) io.observe(finale);
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
